import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useLocation } from "react-router";

/**
 * Open state and keyboard behaviour of the off-canvas nav drawer shown below
 * `md` by `Navbar` and `AdminSidebar` (#992):
 *
 * - closes on route change;
 * - on open, focus moves to the drawer's first nav link;
 * - Escape closes it and returns focus to the toggle button.
 *
 * The drawer itself is `invisible` while closed below `md` (see
 * `drawerVisibilityClass`), which takes it out of the Tab order and the
 * screen-reader tree.
 */
export function useMobileDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const drawerId = useId();

  // Close mobile menu when route changes — intentional side effect on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    drawerRef.current?.querySelector<HTMLElement>("nav a[href]")?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const toggle = useCallback(() => setIsOpen(open => !open), []);
  const close = useCallback(() => setIsOpen(false), []);

  return {
    isOpen,
    toggle,
    close,
    drawerId,
    drawerRef,
    /** Props for the hamburger button. */
    toggleProps: {
      ref: toggleRef,
      onClick: toggle,
      "aria-label": isOpen ? "Close menu" : "Open menu",
      "aria-expanded": isOpen,
      "aria-controls": drawerId,
    },
  };
}

/**
 * Slide + visibility classes for the drawer. Closing transitions visibility
 * with the transform, so the drawer stays visible while it slides out and is
 * hidden (unfocusable, out of the accessibility tree) once offscreen. Opening
 * transitions only the transform: visibility must flip at once, or the first
 * link cannot take focus in the frame the drawer opens. From `md` up the
 * sidebar is always shown.
 */
export const drawerVisibilityClass = (isOpen: boolean): string =>
  `duration-300 ease-in-out ${
    isOpen
      ? "visible translate-x-0 transition-transform"
      : "invisible -translate-x-full transition-[transform,visibility]"
  } md:visible md:translate-x-0`;
